import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type UIEventHandler,
} from "react";

import { useLocaleInfo } from "@/hooks/useLocaleInfo";

function clampIndex(nextIndex: number, itemCount: number) {
  const maxIndex = Math.max(0, itemCount - 1);
  return Math.min(Math.max(nextIndex, 0), maxIndex);
}

export function useScrollCarousel(initialIndex: number) {
  const ref = useRef<HTMLUListElement>(null);
  const targetIndexRef = useRef<number | null>(null);
  const { dir } = useLocaleInfo();
  const isRTL = dir === "rtl";

  const [index, setIndex] = useState(initialIndex);

  // RTL browsers report negative scrollLeft values, so negate the offset
  // when scrolling to the initial position programmatically.
  useLayoutEffect(() => {
    if (ref.current === null) return;

    const safeInitialIndex = clampIndex(
      initialIndex,
      ref.current.children.length,
    );
    setIndex(safeInitialIndex);

    const offset = ref.current.clientWidth * safeInitialIndex;
    ref.current.scrollLeft = isRTL ? -offset : offset;
  }, [initialIndex, isRTL]);

  // If the user touches the element during a programmatic smooth scroll,
  // cancel suppression so the next scroll event syncs index correctly.
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;

    const cancelSuppression = () => {
      targetIndexRef.current = null;
    };

    el.addEventListener("pointerdown", cancelSuppression);
    el.addEventListener("wheel", cancelSuppression, { passive: true });
    el.addEventListener("touchstart", cancelSuppression, { passive: true });

    return () => {
      el.removeEventListener("pointerdown", cancelSuppression);
      el.removeEventListener("wheel", cancelSuppression);
      el.removeEventListener("touchstart", cancelSuppression);
    };
  }, []);

  const handleScroll = useCallback<UIEventHandler<HTMLUListElement>>(
    (event) => {
      const { scrollLeft, clientWidth } = event.currentTarget;
      if (clientWidth <= 0) return;

      // Math.abs converts RTL's negative scrollLeft to a positive offset.
      const newIndex = clampIndex(
        Math.round(Math.abs(scrollLeft) / clientWidth),
        event.currentTarget.children.length,
      );

      // Suppress intermediate updates during programmatic smooth scroll.
      if (targetIndexRef.current !== null) {
        if (newIndex === targetIndexRef.current) {
          targetIndexRef.current = null;
        }
        return;
      }

      setIndex(newIndex);
    },
    [],
  );

  const changeIndex = useCallback(
    (targetIndex: number) => {
      if (ref.current === null) return;

      const safeTargetIndex = clampIndex(
        targetIndex,
        ref.current.children.length,
      );

      // Immediately reflect the target so thumbnails highlight at once,
      // and mark the target so handleScroll ignores intermediate positions.
      targetIndexRef.current = safeTargetIndex;
      setIndex(safeTargetIndex);

      const offset = ref.current.clientWidth * safeTargetIndex;

      ref.current.scrollTo({
        left: isRTL ? -offset : offset,
        behavior: "smooth",
      });
    },
    [isRTL],
  );

  return { ref, index, handleScroll, changeIndex };
}
